import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { formatPrice, formatDate } from "../utils/format";
import { Package, TrendingUp, Clock, CheckCircle, XCircle, Plus } from "lucide-react";

const STATUS_STYLE = {
  pending:  { badge: "badge-warning",  icon: Clock,        label: "Pending Review" },
  approved: { badge: "badge-success",  icon: CheckCircle,  label: "Approved & Live" },
  rejected: { badge: "badge-danger",   icon: XCircle,      label: "Rejected" },
};

export default function BusinessDashboardPage() {
  const { user, profile } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [salesData, setSalesData] = useState({ orders: [], revenue: 0, commission: 0, payout: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    async function load() {
      let subs = [];
      let orderDocs = [];
      try {
        const subSnap = await getDocs(
          query(collection(db, "product_submissions"), where("submittedBy", "==", user.uid), orderBy("createdAt", "desc"))
        );
        subs = subSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (idxErr) {
        console.warn("Submissions index not ready yet, using client sort:", idxErr?.message);
        const fallbackSnap = await getDocs(
          query(collection(db, "product_submissions"), where("submittedBy", "==", user.uid))
        );
        subs = fallbackSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const timeA = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0;
            const timeB = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0;
            return timeB - timeA;
          });
      }
      setSubmissions(subs);

      try {
        const orderSnap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
        orderDocs = orderSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (orderErr) {
        console.warn("Orders query error:", orderErr);
      }

      // Filter orders that contain this seller's products
      const allOrders = orderDocs;
      const myOrders = allOrders.filter(o =>
        (o.items || []).some(item => item.sellerId === user.uid)
      );
      const revenue = myOrders.reduce((s, o) => {
        const myItems = (o.items || []).filter(i => i.sellerId === user.uid);
        return s + myItems.reduce((si, i) => si + (i.price * i.qty), 0);
      }, 0);
      const commission = Math.round(revenue * 0.10);
      const payout = revenue - commission;
      setSalesData({ orders: myOrders, revenue, commission, payout });
      setLoading(false);
    }
    load();
  }, [user]);

  if (loading) return <div className="loader" style={{ minHeight: "60vh" }}><div className="spinner" /></div>;

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <div className="container section-sm" style={{ maxWidth: 920 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700 }}>Seller Dashboard</h1>
            <p style={{ color: "var(--text-muted)", marginTop: 4 }}>{profile?.businessName} · {profile?.email}</p>
          </div>
          <Link to="/business/submit" className="btn btn-primary"><Plus size={16} /> Submit Product</Link>
        </div>

        {/* Commission info banner */}
        <div style={{ padding: 16, background: "var(--accent-light)", border: "1px solid #BFDBFE", borderRadius: 12, marginBottom: 28, fontSize: 14, color: "var(--accent)" }}>
          💰 <strong>Payout Terms:</strong> Afriport retains <strong>10%</strong> commission. Your <strong>90%</strong> is deposited to{" "}
          <strong>{profile?.bankName} — {profile?.accountNumber}</strong> ({profile?.accountName}) within 3–5 business days of each confirmed delivery.
        </div>

        {/* KPI cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 16, marginBottom: 32 }}>
          {[
            { label: "Gross Revenue", value: formatPrice(salesData.revenue), icon: TrendingUp, color: "var(--brand)" },
            { label: "Afriport Commission (10%)", value: formatPrice(salesData.commission), icon: Package, color: "var(--warning)" },
            { label: "Your Earnings (90%)", value: formatPrice(salesData.payout), icon: TrendingUp, color: "var(--success)" },
            { label: "Orders with Your Products", value: salesData.orders.length, icon: Package, color: "var(--accent)" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card" style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.4 }}>{label}</p>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: color + "20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} color={color} />
                </div>
              </div>
              <p style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, color }}>{value}</p>
            </div>
          ))}
        </div>

        {/* Product submissions */}
        <div className="card" style={{ padding: 24, marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18 }}>My Submissions</h2>
            <Link to="/business/submit" className="btn btn-secondary btn-sm"><Plus size={14} /> New</Link>
          </div>

          {submissions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)" }}>
              <Package size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>You haven't submitted any products yet.</p>
              <Link to="/business/submit" className="btn btn-primary" style={{ marginTop: 16 }}>Submit Your First Product</Link>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {submissions.map(sub => {
                const style = STATUS_STYLE[sub.status] || STATUS_STYLE.pending;
                const Icon = style.icon;
                return (
                  <div key={sub.id} style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 56, height: 56, borderRadius: 10, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)", flexShrink: 0 }}>
                      {sub.images?.[0]
                        ? <img src={sub.images[0]} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>📦</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub.name}</div>
                      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{sub.category} · Submitted {formatDate(sub.createdAt)}</div>
                      {sub.status === "rejected" && sub.rejectionReason && (
                        <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 2 }}>Reason: {sub.rejectionReason}</div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "var(--font-head)", fontWeight: 700, marginBottom: 4 }}>{formatPrice(sub.price)}</div>
                      <span className={`badge ${style.badge}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon size={11} /> {style.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bank details */}
        <div className="card" style={{ padding: 24 }}>
          <h2 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Payout Account</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 16 }}>
            {[
              ["Bank", profile?.bankName || "—"],
              ["Account Number", profile?.accountNumber || "—"],
              ["Account Name", profile?.accountName || "—"],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{label}</p>
                <p style={{ fontSize: 15, fontWeight: 500 }}>{value}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 16 }}>
            To update your bank details, please contact <a href="mailto:accounts@afriportb2c.online" style={{ color: "var(--brand)" }}>accounts@afriportb2c.online</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
